import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { CalendarEventModel } from "../../../data/entities";
import { AgendaMode } from "../hooks";
import { AgendaTile } from "./AgendaTile";
import "./AgendaCard.scss";

interface AgendaCardProps {
  title: string;
  mode: AgendaMode;
  selectedDate: Date;
  onModeChange: (mode: AgendaMode) => void;
  onDateChange: (date: Date) => void;
  toolbarRight?: ReactNode;
  events: CalendarEventModel[];
  isLoading: boolean;
  now: Date;
  detailLine: "provider" | "location";
  onEventClick: (event: CalendarEventModel) => void;
  footerTo: string;
  footerLabel: string;
}

export function AgendaCard({
  title,
  mode,
  selectedDate,
  onModeChange,
  onDateChange,
  toolbarRight,
  events,
  isLoading,
  now,
  detailLine,
  onEventClick,
  footerTo,
  footerLabel,
}: Readonly<AgendaCardProps>) {
  const emptyMessage = mode === "upcoming"
    ? "No upcoming appointments"
    : "No appointments on this day";

  return (
    <div className="agenda-card-container d-flex flex-column h-100">
      <div className="mx-3">
        <h2 className="dashboard-title mx-1 my-0">{title}</h2>
      </div>
      <div className="agenda-card d-flex flex-column flex-fill bg-white overflow-hidden m-3 shadow-sm">
        <div className="agenda-toolbar d-flex align-items-center justify-content-between gap-2">
          <div className="agenda-toolbar-views d-flex align-items-center flex-wrap gap-2">
            <div className="agenda-mode-toggle d-inline-flex overflow-hidden">
              <button
                type="button"
                className={`fw-semibold${mode === "upcoming" ? " active" : ""}`}
                onClick={() => onModeChange("upcoming")}
              >
                Upcoming
              </button>
              <button
                type="button"
                className={`fw-semibold${mode === "date" ? " active" : ""}`}
                onClick={() => onModeChange("date")}
              >
                Daily
              </button>
            </div>
            {mode === "date" && (
              <div className="agenda-date-picker">
                <DatePicker
                  value={selectedDate}
                  format="MMM d, yyyy"
                  onChange={(e) => e.value && onDateChange(e.value)}
                />
              </div>
            )}
          </div>
          {toolbarRight}
        </div>
        <div className="agenda-list d-flex flex-column flex-fill gap-2 overflow-y-auto px-3">
          {isLoading ? (
            <>
              <div className="agenda-skeleton flex-shrink-0" />
              <div className="agenda-skeleton flex-shrink-0" />
              <div className="agenda-skeleton flex-shrink-0" />
            </>
          ) : events.length === 0 ? (
            <div className="agenda-empty m-auto p-3 text-center fst-italic">{emptyMessage}</div>
          ) : (
            events.map((event) => (
              <AgendaTile
                key={event.bookingEventId}
                event={event}
                now={now}
                detailLine={detailLine}
                onClick={onEventClick}
              />
            ))
          )}
        </div>
        <Link className="agenda-card-footer d-block text-end px-3 py-2 fw-semibold text-decoration-none" to={footerTo}>
          {footerLabel} <i className="fa fa-arrow-right" />
        </Link>
      </div>
    </div>
  );
}

export default AgendaCard;
